import { useMemo } from "react"
import { makeLink, ComparisonBuilder } from "simple-utils"
import { balanceKind, leadGainRatio, yesterdayLeadGainRatio } from "../../models/Account"
import { FullReportEntry } from "../../models/FullReportEntry"

export type AccountSortingFunction = (a: FullReportEntry, b: FullReportEntry) => number

export interface AccountSorter{
    id: string
    name: string
    sorter: AccountSortingFunction
}

export interface SorterId {
    id: string
    order: "asc" | "desc"
}

type ReportProp = keyof FullReportEntry;
type NumberFn = (account:FullReportEntry) => number;

function reverse(fn: AccountSortingFunction):AccountSortingFunction {
    return (a, b) => fn(a, b) * -1
}

export function createSorter(name: string, sorter: AccountSortingFunction): AccountSorter{
    return {
        id: makeLink(name),
        name,
        sorter
    }
}

export function createNumberSorter(prop:ReportProp, name: string, defaultValue = 0): AccountSorter{
    return createSorter(name, (a, b) => (a[prop] as number ?? defaultValue) - (b[prop] as number ?? defaultValue));
}

export function createNumberFnSorter(fn:NumberFn, name: string, defaultValue = 0): AccountSorter{
    return createSorter(name, (a, b) => (fn(a) ?? defaultValue) - (fn(b) ?? defaultValue));
}

export function createStrSorter(prop:ReportProp, name: string): AccountSorter{
    return createSorter(name, (a, b) => (a[prop] as string ?? "").localeCompare(b[prop] as string ?? ""));
}

export function useAccountSorter(accounts: FullReportEntry[], sortId: SorterId, sorters:AccountSorter[]):FullReportEntry[]{
    return useMemo(() => {
        if(!accounts || !accounts.length) return [];
        if(!sortId) return accounts;
        const sorter = sorters.find( s => s.id == sortId.id);
        if(!sorter) return accounts;
        const result = [...accounts];
        result.sort(sortId.order == 'desc' ? reverse(sorter.sorter) : sorter.sorter);
        return result;

    }, [accounts, sorters, sortId]);
}

export function createBalanceSorter(name:string){
    const d = -1000000
    return createSorter(name, (a, b) =>{
        return new ComparisonBuilder()
            .add(balanceKind(a), balanceKind(b))
            .add(a.balance ?? d, b.balance ?? d)
            .result();
    })
}

export function createLeadGainSorter(name:string){
    const d = -1000000
    return createSorter(name, (a, b) =>{
        return yesterdayLeadGainRatio(a) - yesterdayLeadGainRatio(b)
    })
}